import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Select, Button, Checkbox, Alert, Tag, Spin } from "antd";
import { CheckOutlined, ReloadOutlined, UserSwitchOutlined } from "@ant-design/icons";

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ChangeTl() {
    const { authData, backUrl } = useContext(AuthContext);

    const [tls, setTls] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [reportingEmployees, setReportingEmployees] = useState([]);
    const [outgoingId, setOutgoingId] = useState(null);
    const [incomingId, setIncomingId] = useState(null);
    const [selectedEmpIds, setSelectedEmpIds] = useState([]);
    const [syncHod, setSyncHod] = useState(true);

    const [loadingTls, setLoadingTls] = useState(true);
    const [loadingEmps, setLoadingEmps] = useState(true);
    const [loadingReporting, setLoadingReporting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const headers = { Authorization: `Bearer ${authData?.access}` };

    // ⚡ Fast Fetch TLs List (<50ms)
    const fetchTls = async () => {
        setLoadingTls(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/tl-list/`, { headers });
            setTls(res.data || []);
        } catch {
            setError("Failed to load Team Lead list.");
        } finally {
            setLoadingTls(false);
        }
    };

    // ⚡ Fast Fetch All Active Employees List (<50ms)
    const fetchAllEmployees = async () => {
        setLoadingEmps(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/active-employee-minimal/`, { headers });
            setAllEmployees(res.data || []);
        } catch {
            setError("Failed to load employee list.");
        } finally {
            setLoadingEmps(false);
        }
    };

    // ⚡ Fast Fetch Employees Reporting to Selected TL
    const fetchReportingEmployees = async (tlId) => {
        setLoadingReporting(true);
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        try {
            const res = await axios.get(`${backUrl}/api/emp/tl-employees/${tlId}/`, { headers });
            setReportingEmployees(res.data || []);
        } catch {
            setError("Failed to load reporting team members.");
        } finally {
            setLoadingReporting(false);
        }
    };

    useEffect(() => {
        fetchTls();
        fetchAllEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOutgoingChange = (v) => {
        setOutgoingId(v);
        setIncomingId(null);
        setSuccess("");
        setError("");
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        if (v) fetchReportingEmployees(v);
    };

    const handleToggleEmp = (id) => {
        setSelectedEmpIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleToggleAll = (e) => {
        setSelectedEmpIds(e.target.checked ? reportingEmployees.map((emp) => emp.id) : []);
    };

    const reset = () => {
        setOutgoingId(null);
        setIncomingId(null);
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        setSuccess("");
        setError("");
    };

    const replaceTl = async () => {
        if (!outgoingId || !incomingId || selectedEmpIds.length === 0) {
            setError("Please select Outgoing TL, Incoming TL, and at least one team member.");
            return;
        }

        setSubmitting(true);
        setSuccess("");
        setError("");
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/tl-replacement/`,
                {
                    outgoing_tl_id: Number(outgoingId),
                    incoming_tl_id: Number(incomingId),
                    employee_ids: selectedEmpIds,
                    sync_hod: syncHod,
                },
                { headers }
            );
            setSuccess(res.data?.message || "TL Transfer request submitted for Superuser approval!");
            reset();
            fetchTls();
            fetchAllEmployees();
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Replacement failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const outgoingEmp = tls.find((h) => h.id === outgoingId);
    const incomingEmp = allEmployees.find((e) => e.id === incomingId);
    const allSelected = reportingEmployees.length > 0 && reportingEmployees.every((e) => selectedEmpIds.includes(e.id));
    const canSubmit = outgoingId && incomingId && outgoingId !== incomingId && selectedEmpIds.length > 0 && !submitting;

    return (
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>

            {/* Alerts */}
            {success && (
                <Alert
                    type="success"
                    message={success}
                    showIcon
                    closable
                    onClose={() => setSuccess("")}
                    style={{ marginBottom: 16 }}
                />
            )}
            {error && (
                <Alert
                    type="error"
                    message={error}
                    showIcon
                    closable
                    onClose={() => setError("")}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* TL Selection Row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {/* Outgoing TL */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", display: "block", marginBottom: 6 }}>
                        Outgoing TL <Tag color="red" style={{ marginLeft: 4 }}>OUT</Tag>
                    </label>
                    <Select
                        showSearch
                        placeholder="Search & select outgoing TL"
                        style={{ width: "100%" }}
                        loading={loadingTls}
                        value={outgoingId}
                        onChange={handleOutgoingChange}
                        allowClear
                        filterOption={(input, option) =>
                            String(option?.label || "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={tls
                            .filter((h) => h.id !== incomingId)
                            .map((h) => ({
                                value: h.id,
                                label: h.label,
                            }))
                        }
                    />
                    {outgoingEmp && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, fontSize: 12, color: "#dc2626" }}>
                            <strong>{outgoingEmp.full_name}</strong>
                            <div style={{ color: "#8fa3bc", marginTop: 2 }}>{outgoingEmp.emp_code} · {outgoingEmp.designation_name}</div>
                        </div>
                    )}
                </div>

                {/* Incoming TL */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", display: "block", marginBottom: 6 }}>
                        Incoming TL <Tag color="green" style={{ marginLeft: 4 }}>IN</Tag>
                    </label>
                    <Select
                        showSearch
                        placeholder="Search & select incoming TL"
                        style={{ width: "100%" }}
                        loading={loadingEmps}
                        value={incomingId}
                        onChange={(v) => { setIncomingId(v); setSuccess(""); setError(""); }}
                        allowClear
                        disabled={!outgoingId}
                        filterOption={(input, option) =>
                            String(option?.label || "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={allEmployees
                            .filter((e) => e.id !== outgoingId)
                            .map((e) => ({
                                value: e.id,
                                label: e.label,
                            }))
                        }
                    />
                    {incomingEmp && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, fontSize: 12, color: "#16a34a" }}>
                            <strong>{incomingEmp.full_name}</strong>
                            <div style={{ color: "#8fa3bc", marginTop: 2 }}>{incomingEmp.emp_code} · {incomingEmp.designation_name}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sync HOD Checkbox */}
            <div style={{ marginTop: 16 }}>
                <Checkbox
                    checked={syncHod}
                    onChange={(e) => setSyncHod(e.target.checked)}
                    style={{ fontWeight: 600, color: "#1e3a5f" }}
                >
                    Automatically update reporting HOD to Incoming TL's HOD (Recommended)
                </Checkbox>
            </div>

            {/* Employees Section */}
            {outgoingId && (
                <div style={{ marginTop: 24, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f" }}>
                            Employees Under Outgoing TL ({reportingEmployees.length})
                        </span>
                        {reportingEmployees.length > 0 && (
                            <Checkbox checked={allSelected} onChange={handleToggleAll} style={{ fontSize: 12 }}>
                                Select All ({selectedEmpIds.length}/{reportingEmployees.length})
                            </Checkbox>
                        )}
                    </div>

                    {loadingReporting ? (
                        <div style={{ padding: 24, textAlign: "center" }}>
                            <Spin size="small" />
                            <span style={{ marginLeft: 8, color: "#64748b" }}>Loading team members...</span>
                        </div>
                    ) : reportingEmployees.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                            No active employees report to this TL.
                        </div>
                    ) : (
                        <div style={{ maxHeight: 300, overflowY: "auto" }}>
                            {reportingEmployees.map((emp) => {
                                const checked = selectedEmpIds.includes(emp.id);
                                return (
                                    <div
                                        key={emp.id}
                                        onClick={() => handleToggleEmp(emp.id)}
                                        style={{
                                            padding: "10px 16px",
                                            display: "flex",
                                            alignItems: "center",
                                            justify: "space-between",
                                            borderBottom: "1px solid #f1f5f9",
                                            background: checked ? "#eff6ff" : "transparent",
                                            cursor: "pointer",
                                            transition: "background 0.15s",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <Checkbox
                                                checked={checked}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleToggleEmp(emp.id)}
                                            />
                                            <div>
                                                <span style={{ fontWeight: 500, color: "#0f172a", fontSize: 13 }}>
                                                    {emp.full_name}
                                                </span>
                                                <div style={{ fontSize: 11, color: "#64748b" }}>
                                                    {emp.emp_code} · {emp.designation_name} · {emp.department_name}
                                                </div>
                                            </div>
                                        </div>

                                        <Tag color={checked ? "blue" : "default"} style={{ fontSize: 11 }}>
                                            {checked ? "Transfer" : "Keep"}
                                        </Tag>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={submitting}
                    disabled={!canSubmit}
                    onClick={replaceTl}
                    style={{ flex: 1, backgroundColor: canSubmit ? "#1e3a5f" : undefined }}
                >
                    {submitting ? "Submitting Request..." : `Transfer ${selectedEmpIds.length} Selected Employee(s)`}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={reset}>
                    Reset
                </Button>
            </div>
        </div>
    );
}